import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "react-bootstrap";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import BookingChargesTable from "./Bookingcharttable.jsx";
import { BASEAPIURL, token } from "../env.js";

const fallbackFacilities = [
  {
    image: "https://via.placeholder.com/300x200",
    title: "Sample Facility",
    nav: "SampleFacility",
    images: ["https://via.placeholder.com/300x200"],
    desc: "This is a fallback facility description.",
    branch: [{ branchName: "Main", branchDetails: "Sample details", timming: "9AM - 5PM", branchPrice: "₹500" }],
    key: "fallback-1",
    FacilityGroupID: null,
  },
];

export default function Facility({ showHedeing }) {
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null); // Ref for carousel interval

  // Memoize window height calculation
  const responsiveHeight = useMemo(() => {
    if (windowWidth < 480) return "35vh";
    if (windowWidth < 768) return "50vh";
    if (windowWidth < 1200) return "65vh";
    return "80vh";
  }, [windowWidth]);

  // Optimized shuffle function with useCallback
  const shuffleArray = useCallback((arr) => {
    const array = [...arr];
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }, []);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setLoading(true);
        setError(null); // Reset error on new fetch attempt


        const res = await fetch(`${BASEAPIURL}/GetFGrpListS?Token=${encodeURIComponent(token)}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) throw new Error(`HTTP error: ${res.status}`);

        const result = await res.json();
        console.log(result);
        let facilitiesArray = [];

        if (Array.isArray(result)) {
          facilitiesArray = result;
        } else if (typeof result === "object") {
          const key = Object.keys(result).find(k => Array.isArray(result[k]));
          facilitiesArray = key ? [...result[key]] : []; // Create copy
        }

        // Transform data and create a copy to avoid mutation issues
        const transformed = facilitiesArray.map((f, i) => ({
          image: f.FacilityImages?.[0] || "https://via.placeholder.com/300x200",
          title: f.FacilityName,
          nav: f.FacilityName?.replace(/\s+/g, "") || `facility-${i}`,
          images: f.FacilityImages || [],
          desc: f.FacilityDescShort,
          desc2: f.FacilityDescLong,
          branch: f.branches || f.subFacilities || [],
          key: `facility-${f.FacilityGroupID || i}-${i}`,
          FacilityGroupID: f.FacilityGroupID || null,
        }));

        setFacilities(transformed);

        // Prepare carousel images with limits to prevent performance issues
        const banners = transformed
          .flatMap((f) => f.images.slice(0, 3)) // Limit images per facility
          .slice(0, 30) // Limit total carousel images
          .map((img, idx) => ({
            src: img,
            alt: `Carousel image ${idx + 1}`,
            key: `carousel-${idx}`,
          }));

        setCarouselImages(shuffleArray(banners)); // Shuffle once when setting
      } catch (err) {
        console.error("Fetch failed, using fallback:", err.message);
        setError("Network issue. Showing fallback data.");
        setFacilities(fallbackFacilities);
        setCarouselImages([
          {
            src: fallbackFacilities[0].image,
            alt: fallbackFacilities[0].title,
            key: "fallback-carousel",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchFacilities();
  }, [shuffleArray]); // Add shuffleArray as dependency

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Improved carousel interval management with useRef
  useEffect(() => {
    if (carouselImages.length === 0) return;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set new interval
    intervalRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % carouselImages.length);
    }, 3500);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [carouselImages]); // Depend on the full array

  // Memoized navigation handler
  const handleClick = useCallback((facility) => {
    navigate(`/Facility/${encodeURIComponent(facility.nav)}`, {
      state: facility,
    });
  }, [navigate]);

  // Memoized Facility Card Component
  const FacilityCard = useMemo(() => React.memo(({ facility, index, isHovered }) => (
    <div className="col-lg-3 col-md-4 col-sm-6 d-flex justify-content-center">
      <Card
        onClick={() => handleClick(facility)}
        onMouseEnter={() => setHoveredIndex(index)}
        onMouseLeave={() => setHoveredIndex(null)}
        style={{
          padding: "10px",
          width: "100%",
          maxWidth: "350px",
          borderRadius: "12px",
          cursor: "pointer",
          textAlign: "center",
          position: "relative", // Needed for absolute positioning of indicator
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: isHovered ? 0 : "50%",
            width: isHovered ? "100%" : 0,
            height: "4px",
            backgroundColor: "#0d6efd",
            borderRadius: "2px",
            transition: "width 0.3s ease, left 0.3s ease",
            zIndex: 2, // Ensure it's above the image
          }}
        />
        <LazyLoadImage
          src={facility.image}
          alt={facility.title}
          effect="blur"
          style={{
            width: "100%",
            height: "200px",
            objectFit: "cover",
            borderRadius: "8px",
          }}
        />
        <div style={{
          paddingTop: "10px",
          fontSize: "1.2em",
          fontWeight: "600",
          color: isHovered ? "#0d6efd" : "#b5835c"
        }}>
          {facility.title}
        </div>
        <div style={{
          padding: "5px",
          fontSize: "14px",
          color: "#000",
          textAlign: "justify",
          lineHeight: "2em"
        }}>
          {facility.desc}
        </div>
        {facility.branch.length > 0 && (
          <div style={{
            padding: "5px",
            fontSize: "12px",
            color: "#555",
            textAlign: "left"
          }}>
            <strong>Branches:</strong>
            <ul className="mb-0"> {/* Remove default margin */}
              {facility.branch.slice(0, 3).map((b, bi) => ( // Limit displayed branches
                <li key={`branch-${facility.key}-${bi}`}>
                  {b.branchName}: {b.branchDetails}
                  {b.timming && ` (${b.timming})`}
                  {b.branchPrice && ` - ${b.branchPrice}`}
                </li>
              ))}
              {facility.branch.length > 3 && (
                <li className="text-muted">... and {facility.branch.length - 3} more</li>
              )}
            </ul>
          </div>
        )}
      </Card>
    </div>
  )), [handleClick]); // Only re-create if handleClick changes

  // Memoized rendering of facility cards
  const facilityCards = useMemo(() => {
    return facilities.map((facility, index) => (
      <FacilityCard
        key={facility.key}
        facility={facility}
        index={index}
        isHovered={hoveredIndex === index}
      />
    ));
  }, [facilities, hoveredIndex, FacilityCard]); // Re-run when these change

  // Memoized list items for showHedeing
  const facilityListItems = useMemo(() => {
    return facilities.map((f) => (
      <li
        className="list-group-item d-flex justify-content-between align-items-start"
        key={`list-${f.key}`}
      >
        <strong>{f.title}</strong>
        {f.branch.length > 0 && (
          <span>
            ({f.branch.slice(0, 2).map(b => b.branchName).join(", ")})
            {f.branch.length > 2 && ` +${f.branch.length - 2}`}
          </span>
        )}
      </li>
    ));
  }, [facilities]); // Re-run only when facilities change

  return (
    <div className="container-fluid w-100 p-0 m-0">
      {loading ? (
        <>
          <div className="row p-4 g-3">
            {Array.from({ length: 1 }).map((_, i) => (
              <div className="col-12" key={i}>
                {/* 🔹 Image Placeholder */}
                <div className="placeholder-glow">
                  <div
                    className="card-img-top bg-secondary placeholder position-relative"
                    style={{
                      height: "70vh",            // Default height for medium/large screens
                    }}
                  >
                    <div
                      className="position-absolute top-50 start-50 translate-middle text-center text-white p-3 p-md-4"
                      style={{ zIndex: 10, maxWidth: "90%" }} // Keep text inside screen
                    >
                      <h1 className="fw-bold display-6 display-md-4">
                        Explore Our Club Facilities
                      </h1>
                    </div>
                  </div>
                </div>

              </div>
            ))}
          </div>
          <div className="row p-4 g-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="col-md-3 col-sm-6" key={i}>
                <div className="card shadow-sm border-0">
                  {/* 🔹 Image Placeholder */}
                  <div className="placeholder-glow">
                    <div
                      className="card-img-top bg-secondary placeholder"
                      style={{ height: "200px" }}
                    ></div>
                  </div>

                  {/* 🔹 Text Placeholder */}
                  <div className="card-body">
                    <h5 className="card-title placeholder-glow mb-3">
                      <span className="placeholder col-6"></span>
                    </h5>
                    <p className="card-text placeholder-glow">
                      <span className="placeholder col-7"></span>
                      <span className="placeholder col-4"></span>
                      <span className="placeholder col-6"></span>
                      <span className="placeholder col-4"></span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : error ? (
        <div className="text-center my-4 text-danger">
          <h5>Error: {error}</h5>
        </div>
      ) : facilities.length > 0 ? (
        <>
          {/* 🔹 Hero Banner */}
          <div
            className=" position-relative overflow-hidden"
            style={{ maxHeight: responsiveHeight, zIndex: 0 }}
          >
            <div
              className="position-absolute top-50 start-50 translate-middle text-center text-white p-4"
              style={{
                zIndex: 10,
              }}
            >
              <h1 className="fw-bold">Explore Our Club Facilities</h1>
            </div>

            {/* 🔹 Carousel */}
            <div
              className="w-100 position-relative overflow-hidden"
              style={{ maxHeight: responsiveHeight, zIndex: 0 }}
            >
              <div
                className="position-absolute top-50 start-50 translate-middle text-center text-white"
                style={{
                  zIndex: 10,
                  background: 'rgba(0, 0, 0, 0.4)',
                  padding: '2rem',
                  borderRadius: '12px',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <h1 className="fw-bold">Explore Our Club Facilities</h1>
              </div>

              {/* Carousel */}
              <div className="carousel slide w-100">
                <div className="carousel-inner">
                  {carouselImages.map((item, index) => (
                    <div
                      className={`carousel-item ${index === activeIndex ? "active" : ""}`}
                      key={item.key}
                    >
                      <LazyLoadImage
                        src={item.src}
                        alt={item.alt}
                        effect="blur"
                        className="d-block"
                        style={{
                          width: "100vw",
                          height: responsiveHeight,
                          objectFit: "cover",
                          objectPosition: "center",
                          transform: "scale(1.05)",
                          transition: "transform 0.5s ease-in-out",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* 🔹 Facility Cards */}
          <div className="row  justify-content-center p-3 g-3">
            {facilityCards}
          </div>

          {showHedeing && (
            <>
              <div className="row " />
              <div className="d-flex justify-content-center">
                <BookingChargesTable />
              </div>
            </>
          )}
        </>
      ) : null}
    </div>

  );
}